import {useCallback, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  useAddConfirmedStep,
  useGetCampaignById,
  usePatchCampaign,
  usePreviewAudienceChange,
  useValidateCampaignAudience,
} from '@tinct/core-api';
import {useCurrentWorkspaceUrlPrefix, useGetCurrentWorkspaceId} from '@tinct/core-workspace';
import {pluralize} from '@tinct/core-utils';
import {AudienceConflictDto} from '@tinct/core-api-models';
import {AudienceStepState, Step4Audience} from '../step4-audience';
import {AudienceConflictModal} from '../audience-conflict-modal';
import {AudienceSummary} from '../audience-types';
import {ChangesNotLiveModal} from '../../../../modals/changes-not-live-modal/changes-not-live-modal';
import {useGenerateAllPages} from '../../../../hooks/use-generate-all-pages';
import {campaignHasGeneratedPages} from '../../../../utils/campaign-has-generated-pages';

type UseCreateStep4AudienceOptions = {
  campaignId: string;
};

export const useCreateStep4Audience = ({campaignId}: UseCreateStep4AudienceOptions) => {
  const navigate = useNavigate();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const workspaceId = useGetCurrentWorkspaceId();
  const [state, setState] = useState<AudienceStepState>({
    selectedAudienceId: null,
    selectedAudience: null,
    currentAudience: null,
    isPending: false,
    lastImportJobStatus: null,
  });
  const [conflicts, setConflicts] = useState<AudienceConflictDto[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);
  // Informative "changes aren't live yet" popup, shown after a live campaign's
  // audience is changed (replaces the old pre-save impact dialog).
  const [showChangesNotLive, setShowChangesNotLive] = useState(false);
  const [newPagesCount, setNewPagesCount] = useState(0);

  const {data: campaign} = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        status: null,
        statistics: {
          fields: {
            totalPagesCount: null,
          },
        },
      },
    },
    {enabled: !!campaignId},
  );
  const isLiveOrHasBeenLiveWithPages = campaignHasGeneratedPages(campaign);

  // Latest audience summaries from the step, kept in a ref so the frequent
  // onStateChange updates never change `component`'s identity (which would
  // remount Step4Audience on every render and loop).
  const summariesRef = useRef<{current: AudienceSummary | null; selected: AudienceSummary | null}>({
    current: null,
    selected: null,
  });

  // Whether the last save actually switched audience. `saveAndContinue` works
  // this out, but the conflict-modal follow-up runs in a separate callback
  // afterwards and needs the same answer to decide whether the "changes aren't
  // live" notice (and its page count) means anything.
  const isAudienceChangeRef = useRef(false);

  const {mutateAsync: patchCampaign, isPending} = usePatchCampaign();
  const {mutateAsync: validateAudience, isPending: validateAudiencePending} = useValidateCampaignAudience();
  const {mutateAsync: previewAudienceChange, isPending: previewPending} = usePreviewAudienceChange();
  const {mutateAsync: addConfirmedStep} = useAddConfirmedStep();
  const {generateAllAndReview} = useGenerateAllPages(campaignId);

  const handleStateChange = useCallback((newState: Partial<AudienceStepState>) => {
    const {selectedAudience, currentAudience, ...rest} = newState;
    if (selectedAudience !== undefined) summariesRef.current.selected = selectedAudience;
    if (currentAudience !== undefined) summariesRef.current.current = currentAudience;
    setState(prev => ({...prev, ...rest}));
  }, []);

  const continueToSampling = useCallback(async () => {
    await addConfirmedStep({id: campaignId, confirmedStep: 'audience'});
    navigate(`${currentWorkspaceUrlPrefix}/funnel-campaigns/${campaignId}/sampling`, {replace: true});
  }, [addConfirmedStep, campaignId, navigate, currentWorkspaceUrlPrefix]);

  // The user reached this dialog by pressing "Next step", so "Continue" has to
  // finish that move. Merely dismissing would drop them back on the audience
  // step with no feedback and no confirmed step recorded.
  const continueFromChangesNotLive = useCallback(async () => {
    setShowChangesNotLive(false);
    await continueToSampling();
  }, [continueToSampling]);

  const goGenerateAndReview = useCallback(async () => {
    // The quota gate lives in the hook: when credits fall short it opens the
    // paywall and skips `onStart`, leaving this modal up instead of navigating.
    await generateAllAndReview({onStart: () => setShowChangesNotLive(false)});
  }, [generateAllAndReview]);

  const saveAndContinue = useCallback(async () => {
    const selected = summariesRef.current.selected;
    const selectedAudienceId = selected?.id;
    if (!selectedAudienceId) return;

    try {
      // Only treat as a change when switching away from a different audience.
      const current = summariesRef.current.current;
      const isChange = !!current && current.id !== selectedAudienceId;
      isAudienceChangeRef.current = isChange;

      // Best-effort preview of how many new variant pages the change creates,
      // used in the informative popup. Falls through if the endpoint fails —
      // cleared first so a failure can never surface an earlier change's count.
      if (isChange) {
        setNewPagesCount(0);
        try {
          const impact = await previewAudienceChange({
            id: campaignId,
            audienceId: selectedAudienceId,
          });
          setNewPagesCount(impact?.newCompaniesCount ?? 0);
        } catch (e) {
          console.error('Error previewing audience change:', e);
        }
      }

      await patchCampaign({
        id: campaignId,
        data: {
          audience: {id: selectedAudienceId},
        },
      });

      const result = await validateAudience({
        id: campaignId,
        organizationId: workspaceId,
        excludeConflicts: false,
      });

      if (result.conflicts && result.conflicts.length > 0) {
        setConflicts(result.conflicts);
        setShowConflictModal(true);
        return;
      }

      // After a real change to a live campaign, inform the user their changes
      // aren't live yet rather than advancing the funnel automatically.
      if (isChange && isLiveOrHasBeenLiveWithPages) {
        setShowChangesNotLive(true);
        return;
      }

      await continueToSampling();
    } catch (e) {
      console.error('Error saving audience and continuing:', e);
    }
  }, [previewAudienceChange, patchCampaign, campaignId, validateAudience, workspaceId, isLiveOrHasBeenLiveWithPages, continueToSampling]);

  const handleExcludeAndContinue = useCallback(async () => {
    try {
      await validateAudience({
        id: campaignId,
        organizationId: workspaceId,
        excludeConflicts: true,
      });
      setShowConflictModal(false);
      setConflicts([]);

      // Same condition as `saveAndContinue`: the notice is only meaningful when
      // this save actually switched audience — otherwise nothing outdated the
      // live pages and `newPagesCount` was never computed.
      if (isAudienceChangeRef.current && isLiveOrHasBeenLiveWithPages) {
        setShowChangesNotLive(true);
        return;
      }

      await continueToSampling();
    } catch (e) {
      console.error('Error excluding conflicts and continuing:', e);
    }
  }, [validateAudience, campaignId, workspaceId, isLiveOrHasBeenLiveWithPages, continueToSampling]);

  // The funnel renders `component` as a component *type* (`<Step />`), so its
  // identity must stay stable — otherwise React remounts the whole step on every
  // state change. The modals therefore live in `modals` (rendered in a stable,
  // always-mounted slot), keeping this purely the step body.
  const component = useCallback(
    () => <Step4Audience onStateChange={handleStateChange} />,
    [handleStateChange]
  );

  // Rendered by the funnel host in a persistent slot (not through `component`)
  // so toggling a modal open never remounts the step and makes it flicker.
  const modals = (
    <>
      <AudienceConflictModal
        open={showConflictModal}
        onOpenChange={setShowConflictModal}
        conflicts={conflicts}
        onExcludeAndContinue={handleExcludeAndContinue}
        isPending={validateAudiencePending}
      />
      <ChangesNotLiveModal
        open={showChangesNotLive}
        onOpenChange={(next) => !next && setShowChangesNotLive(false)}
        step="the audience"
        alertDescription={`${newPagesCount} new variant ${pluralize(
          newPagesCount,
          'page'
        )} ${pluralize(newPagesCount, 'needs', 'need')} to be generated.`}
        actions={[
          { label: 'Continue', onClick: continueFromChangesNotLive },
          { label: 'Generate & review', onClick: goGenerateAndReview },
        ]}
      />
    </>
  );

  return {
    component,
    modals,
    selectedAudienceId: state.selectedAudienceId,
    isAudienceSelected: !!state.selectedAudienceId,
    isAudienceImportCompleted: state.lastImportJobStatus === 'completed',
    isPending: isPending || validateAudiencePending || previewPending,
    saveAndContinue,
  };
};
